import React from 'react';
import cx from 'classnames';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';
import config from '@plone/volto/registry';
import { isInternalURL, flattenToAppURL } from '@plone/volto/helpers/Url/Url';

const IMAGE_EXTS: string[] = [
  'png',
  'jpg',
  'jpeg',
  'gif',
  'svg',
  'webp',
  'avif',
  'heic',
  'heif',
  'bmp',
  'tiff',
  'tif',
  'ico',
];

const FILE_EXTS: string[] = [
  'doc',
  'docx',
  'xls',
  'xlsx',
  'ppt',
  'pptx',
  'zip',
  'rar',
  '7z',
  'csv',
  'txt',
  'rtf',
  'odt',
  'ods',
  'odp',
];

const getPublicApiBase = (): string => {
  let api = String((config.settings as any)?.apiPath || '').trim();

  if (!api) {
    if (typeof window !== 'undefined' && window.location?.origin) {
      return `${window.location.origin}/++api++`;
    }
    return '/++api++';
  }

  if (api.includes('/++api++')) {
    return api.replace(/\/\+\+api\+\+\/*$/, '/++api++');
  }

  return api.replace(/\/+$/, '') + '/++api++';
};

const toPathOnly = (href: string): string => {
  try {
    const u = new URL(href, window.location.origin);

    if (u.origin === window.location.origin) {
      return u.pathname + u.search + u.hash;
    }
  } catch {}
  return href;
};

const stripLeadingApi = (path: string): string => {
  return String(path || '').replace(/^\/\+\+api\+\+\/+/, '/');
};

// join paths ensuring there's exactly one slash between them
const urlJoin = (base: string, ...parts: string[]): string => {
  let acc = String(base || '');
  for (const p of parts) {
    const seg = String(p || '');
    acc = acc.replace(/\/+$/, '') + '/' + seg.replace(/^\/+/, '');
  }
  return acc;
};

// collapse multiple slashes (except for the "://" part)
const collapseSlashes = (url: string): string => {
  return String(url || '').replace(/([^:]\/)\/+/g, '$1');
};

const isApiHref = (href: string): boolean => {
  return typeof href === 'string' && /\/\+\+api\+\+(?:\/|$)/.test(href);
};

export interface NormalizeResult {
  href: string;
  isPdf: boolean;
  isDownload: boolean;
}

export interface ViewLinkProps {
  url: string;
  target?: string;
  download?: boolean;
  children: React.ReactNode;
  openLinkInNewTab?: boolean;
}

export interface LinkElementProps {
  attributes: Record<string, any>;
  children: React.ReactNode;
  mode?: 'edit' | 'view';
  element: {
    data?: {
      url?: string | { url?: string };
      openLinkInNewTab?: boolean;
      target?: string;
      download?: boolean;
      [key: string]: any;
    };
  };
}

export const normalizeHref = (rawHref = ''): NormalizeResult => {
  if (!rawHref) return { href: rawHref, isPdf: false, isDownload: false };

  if (!isInternalURL(rawHref)) {
    return { href: rawHref, isPdf: false, isDownload: false };
  }

  const hrefPath = toPathOnly(rawHref);
  const pathPart = hrefPath.split(/[?#]/)[0];
  const suffix = hrefPath.slice(pathPart.length);
  const withoutTrailing = pathPart.replace(/\/$/, '');

  const base = withoutTrailing.replace(
    /\/@@(?:display-file|inline-file|download)\/(?:file|image)$/,
    '',
  );

  const baseNoApi = stripLeadingApi(base);
  const ext = (baseNoApi.split('.').pop() || '').toLowerCase();

  if (ext === 'pdf') {
    const apiBase = getPublicApiBase();
    const joined = urlJoin(apiBase, baseNoApi, '@@inline-file', 'file');
    const pdfApiHref = collapseSlashes(joined + suffix);
    return {
      href: pdfApiHref,
      isPdf: true,
      isDownload: false,
    };
  }

  if (IMAGE_EXTS.includes(ext)) {
    const dl = collapseSlashes(`${baseNoApi}/@@download/image${suffix}`);
    return {
      href: dl,
      isPdf: false,
      isDownload: true,
    };
  }

  if (FILE_EXTS.includes(ext)) {
    const dl = collapseSlashes(`${baseNoApi}/@@download/file${suffix}`);
    return {
      href: dl,
      isPdf: false,
      isDownload: true,
    };
  }

  return { href: rawHref, isPdf: false, isDownload: false };
};

const ViewLink: React.FC<ViewLinkProps> = ({
  url,
  target,
  download,
  children,
  openLinkInNewTab,
}) => {
  const { openExternalLinkInNewTab } = config.settings as any;
  const { href, isPdf, isDownload } = normalizeHref(url);

  const computedOpenInNewTab = isPdf
    ? true
    : openLinkInNewTab ??
      ((openExternalLinkInNewTab && !isInternalURL(href)) ||
        target === '_blank');

  const finalHref = isApiHref(href) ? href : flattenToAppURL(href);

  return (
    <UniversalLink
      href={finalHref}
      openLinkInNewTab={computedOpenInNewTab}
      download={isDownload || download}
      // @ts-expect-error rel is not defined in UniversalLinkProps, but we want to set it for security reasons when opening in new tab
      rel={computedOpenInNewTab ? 'noopener noreferrer' : undefined}
    >
      {children}
    </UniversalLink>
  );
};

export const LinkElement: React.FC<LinkElementProps> = (props) => {
  const { attributes, children, element, mode = 'edit' } = props;

  const rawUrl = element?.data?.url;
  const url =
    typeof rawUrl === 'string'
      ? rawUrl
      : typeof (rawUrl as any)?.url === 'string'
        ? (rawUrl as any).url
        : '';

  const internal = isInternalURL(url);

  return mode === 'view' ? (
    <ViewLink {...((element.data || {}) as ViewLinkProps)}>{children}</ViewLink>
  ) : (
    <a
      {...attributes}
      className={cx('slate-editor-link', { external: !internal })}
      href={internal ? flattenToAppURL(url) : url}
      onClick={(e) => e.preventDefault()}
    >
      {Array.isArray(children)
        ? children.map((child, i) => {
            if ((child as any)?.props?.decorations) {
              const isSelection =
                (child as any).props.decorations.findIndex(
                  (deco: any) => deco.isSelection,
                ) > -1;
              if (isSelection) {
                return (
                  <span className="highlight-selection" key={`${i}-sel`}>
                    {child}
                  </span>
                );
              }
            }
            return child as any;
          })
        : children}
    </a>
  );
};
